import { useState, useRef } from 'react';
import { Text, Box, SimpleGrid, ActionIcon, Overlay, Stack } from '@mantine/core';
import styles from './ImageUploader.module.css';

const PhotoSlot = ({ config, value, onChange, onRemove, error }) => {
  const inputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onChange(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onChange(file);
    }
  };

  const previewUrl = value ? URL.createObjectURL(value) : null;
  const displayImage = previewUrl || config.exampleImage;
  const hasImage = !!displayImage;
  const photoSlotClassName = [
    styles.photoSlot,
    isDragOver && styles.dragOver,
    previewUrl && styles.hasPreview,
    error && styles.hasError,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={styles.photoSlotWrapper}>
      <Box
        className={photoSlotClassName}
        onClick={handleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {hasImage ? (
          <>
            <img
              src={displayImage}
              alt={config.label}
              className={styles.photoSlotImage}
            />
            {!previewUrl && config.exampleImage && (
              <Overlay
                gradient="linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 50%)"
                zIndex={1}
              />
            )}
            {!previewUrl && (
              <Box className={styles.photoSlotOverlayText}>
                <Text size="xs" c="white" fw={500}>
                  Click to upload
                </Text>
              </Box>
            )}
          </>
        ) : (
          <Stack align="center" justify="center" className={styles.photoSlotEmpty}>
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <Text size="xs" c="dimmed" ta="center">
              Click or drag
            </Text>
          </Stack>
        )}

        {previewUrl && (
          <ActionIcon
            variant="filled"
            color="red"
            size="sm"
            radius="xl"
            className={styles.photoSlotRemove}
            onClick={(e) => {
              e.stopPropagation();
              URL.revokeObjectURL(previewUrl);
              onRemove();
            }}
            aria-label="Remove image"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </ActionIcon>
        )}

        {/* Success indicator */}
        {previewUrl && (
          <Box className={styles.photoSlotSuccess}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </Box>
        )}
      </Box>

      <Text size="sm" ta="center" mt="xs" fw={500} c={error ? 'red' : config.required ? undefined : 'dimmed'}>
        {config.label}
        {config.required && <Text component="span" c="red" ml={2}>*</Text>}
      </Text>
      {config.description && (
        <Text size="xs" ta="center" c="dimmed" mt={2}>
          {config.description}
        </Text>
      )}
      {error && (
        <Text size="xs" ta="center" c="red" mt={2} fw={500}>
          {error}
        </Text>
      )}
    </Box>
  );
};

export const ImageUploader = ({ photos = [], onImagesChange, errors = {}, columns, gap }) => {
  const [images, setImages] = useState({});

  const handleImageChange = (name, file) => {
    const newImages = { ...images, [name]: file };
    setImages(newImages);
    onImagesChange(newImages);
  };

  const handleImageRemove = (name) => {
    const newImages = { ...images };
    delete newImages[name];
    setImages(newImages);
    onImagesChange(newImages);
  };

  if (!photos.length) return null;

  return (
    <Box>
      <Text fw={600} mb="md" size="sm" tt="uppercase" c="dimmed" style={{ letterSpacing: '0.5px' }}>
        Images
      </Text>
      <SimpleGrid cols={columns} spacing={gap}>
        {photos.map((photo) => (
          <PhotoSlot
            key={photo.name}
            config={photo}
            value={images[photo.name]}
            onChange={(file) => handleImageChange(photo.name, file)}
            onRemove={() => handleImageRemove(photo.name)}
            error={errors[photo.name]}
          />
        ))}
      </SimpleGrid>
    </Box>
  );
};
